/**
 * InstMcqRoute — candidate-facing MCQ campaign page.
 * URL: /inst/mcq/:campaignId
 *
 * Flow:
 *   1. Load campaign (includes pre-generated questions + optional coding problems)
 *   2. Require Firebase login
 *   3. Run MCQ — uses pre-generated question cache; falls back to API generation
 *   4. After MCQ scorecard: if campaign has coding problems, show "Start Coding" bar
 *   5. Coding phase: same CodingTest component used by InstCodingRoute
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";
import { getAuthToken } from "./api";
import AssessmentScreen from "./AssessmentScreen";
import { CodingTest } from "./InstCodingRoute";
import useCampaignProctoring from "./hooks/useCampaignProctoring";
import "./InstitutionalDashboard.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

// ─── Campaign top bar ─────────────────────────────────────────────────────────

function CampaignTopBar({ integrityScore, integrityLevel, deadlineEpoch }) {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(() =>
    deadlineEpoch ? Math.max(0, Math.floor((deadlineEpoch - Date.now()) / 1000)) : null
  );

  useEffect(() => {
    if (!deadlineEpoch) return;
    setTimeLeft(Math.max(0, Math.floor((deadlineEpoch - Date.now()) / 1000)));
    const id = setInterval(() => {
      setTimeLeft(Math.max(0, Math.floor((deadlineEpoch - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [deadlineEpoch]);

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const handleLogout = async () => {
    await signOut(auth).catch(() => {});
    navigate("/login");
  };
  return (
    <div className="inst-topbar">
      <div className="inst-topbar__brand">
        <img src="/Vagmee_Logo.png" alt="Vagmee" className="inst-topbar__logo" />
        <span className="inst-topbar__name">Vagmee</span>
      </div>
      <div className="inst-topbar__right">
        {timeLeft !== null && (
          <span className={`inst-topbar__global-timer${timeLeft < 300 ? " inst-topbar__global-timer--warn" : ""}`}>
            ⏱ {fmtTime(timeLeft)}
          </span>
        )}
        {integrityScore !== undefined && (
          <span className={`inst-topbar__integrity lvl-${integrityLevel}`}>
            ◉ Integrity {integrityScore}
          </span>
        )}
        <button className="inst-topbar__logout" onClick={handleLogout}>Logout</button>
      </div>
    </div>
  );
}

// ─── Self-view camera overlay ─────────────────────────────────────────────────

function SelfViewCamera({ onStream, onStatus }) {
  const videoRef = useRef(null);
  const [status, setStatus] = useState("requesting");

  useEffect(() => {
    let stream = null;
    navigator.mediaDevices
      .getUserMedia({ video: { width: 160, height: 120, facingMode: "user" }, audio: false })
      .then((s) => {
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play().catch(() => {});
        }
        setStatus("active");
        onStream?.(s);
        onStatus?.("active");
      })
      .catch(() => { setStatus("denied"); onStatus?.("denied"); });

    return () => {
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  if (status === "denied") {
    return (
      <div className="inst-selfview inst-selfview--denied">
        <span>📷</span>
        <p>Camera denied</p>
      </div>
    );
  }

  return (
    <div className="inst-selfview">
      <video ref={videoRef} className="inst-selfview__video" muted playsInline autoPlay />
      <div className="inst-selfview__label">
        <span className="inst-selfview__dot" />
        Proctored
      </div>
    </div>
  );
}

// ─── Screen-share + fullscreen gate (proctoring) ───────────────────────────
// Shown before the MCQ test renders. Screen share MUST come first — Chrome's
// native "choose what to share" picker itself drops the page out of
// fullscreen, so requesting fullscreen first only gets silently undone the
// moment the share picker opens. Two sequential steps, each requiring its
// own user gesture (both prompts need one).

function ProctoringGateCard({
  screenShareStatus, onShareScreen,
  fullscreenStatus, onEnterFullscreen, fullscreenAttempts, onSkipFullscreen,
}) {
  const screenShareDone = screenShareStatus === "active";

  return (
    <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
      <div className="inst-mcq-error-icon">🖥️</div>
      <h2>Proctoring Setup</h2>
      <p>This assessment requires screen sharing and fullscreen mode so it can be proctored.</p>

      {!screenShareDone ? (
        <>
          <button className="inst-btn-primary" style={{ marginTop: "1.25rem" }} onClick={onShareScreen}>
            {screenShareStatus ? "Try Again" : "Share Your Screen"}
          </button>
          {screenShareStatus === "denied" && (
            <p style={{ marginTop: "0.75rem", color: "#f87171", fontSize: "0.85rem" }}>
              Screen sharing is required to continue. Please select a screen and click Share.
            </p>
          )}
          {screenShareStatus === "wrong_surface" && (
            <p style={{ marginTop: "0.75rem", color: "#f87171", fontSize: "0.85rem" }}>
              Please share your <strong>Entire Screen</strong>, not a specific window or tab.
            </p>
          )}
        </>
      ) : (
        <>
          <p style={{ color: "#36c8a0", fontSize: "0.9rem" }}>✓ Screen sharing active</p>
          <button className="inst-btn-primary" style={{ marginTop: "0.5rem" }} onClick={onEnterFullscreen}>
            {fullscreenAttempts > 0 ? "Try Again" : "Enter Fullscreen"}
          </button>
          {fullscreenStatus === "failed" && (
            <>
              <p style={{ marginTop: "0.75rem", color: "#f87171", fontSize: "0.85rem" }}>
                Could not enter fullscreen mode.
              </p>
              {fullscreenAttempts > 0 && (
                <button className="link-button" style={{ marginTop: "0.25rem" }} onClick={onSkipFullscreen}>
                  Continue without fullscreen
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main route ───────────────────────────────────────────────────────────────

export default function InstMcqRoute() {
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(true);
  const [campaignError, setCampaignError] = useState(null);
  const [currentUser, setCurrentUser] = useState(undefined);
  const [alreadyAttempted, setAlreadyAttempted] = useState(null); // null=checking, true/false

  // "mcq" → MCQ in progress | "coding" → seamless coding section after MCQ
  const [outerPhase, setOuterPhase] = useState("mcq");
  const [mcqScore, setMcqScore] = useState(null);
  const [testStarted, setTestStarted] = useState(false);
  const [assessmentDone, setAssessmentDone] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraStatus, setCameraStatus] = useState(null); // null|"active"|"denied"
  const [fullscreenStatus, setFullscreenStatus] = useState(null); // null|"active"|"failed"|"skipped"
  const [fullscreenAttempts, setFullscreenAttempts] = useState(0);
  const [screenStream, setScreenStream] = useState(null);
  const [screenShareStatus, setScreenShareStatus] = useState(null); // null|"active"|"denied"
  // 65-minute global deadline shared across MCQ + coding phases.
  // Persisted in sessionStorage so a page refresh doesn't reset the clock.
  const [deadlineEpoch, setDeadlineEpoch] = useState(() => {
    const stored = sessionStorage.getItem(`vagmee_deadline_${campaignId}`);
    return stored ? parseInt(stored, 10) : null;
  });

  const handleEnterFullscreen = async () => {
    try {
      await document.documentElement.requestFullscreen();
      setFullscreenStatus("active");
    } catch (_) {
      setFullscreenStatus("failed");
      setFullscreenAttempts((n) => n + 1);
    }
  };

  const handleShareScreen = async () => {
    try {
      // displaySurface: "monitor" hints the browser's picker toward "Entire
      // Screen" (Chrome pre-selects that tab of the picker) — it's a hint,
      // not an enforceable restriction, so the actual selection is still
      // validated below and rejected if the candidate picked a window/tab.
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "monitor" },
        audio: false,
      });
      const track = stream.getVideoTracks()[0];
      const surface = track?.getSettings?.().displaySurface;
      if (surface && surface !== "monitor") {
        stream.getTracks().forEach((t) => t.stop());
        setScreenShareStatus("wrong_surface");
        return;
      }
      setScreenStream(stream);
      setScreenShareStatus("active");
    } catch (_) {
      setScreenShareStatus("denied");
    }
  };

  useEffect(() => {
    return () => { screenStream?.getTracks().forEach((t) => t.stop()); };
  }, [screenStream]);

  // Questions and coding problems are loaded separately from metadata.
  const [prefilledQuestions, setPrefilledQuestions] = useState(null);
  const [questionsPolling, setQuestionsPolling] = useState(false);
  const [codingProblems, setCodingProblems] = useState([]);

  // Load the candidate's server-assigned question version after auth is ready.
  useEffect(() => {
    if (!currentUser) return undefined;
    let cancelled = false;
    async function load() {
      try {
        const token = await getAuthToken();
        const [publicRes, questionsRes] = await Promise.all([
          fetch(`${API_BASE}/api/institutional/campaigns/${campaignId}/public`),
          fetch(`${API_BASE}/api/institutional/campaigns/${campaignId}/questions`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        if (!publicRes.ok) {
          const d = await publicRes.json().catch(() => ({}));
          throw new Error(
            d.detail || d.message ||
            (publicRes.status === 410 ? "This assessment link is no longer active." : "Campaign not found.")
          );
        }

        if (!cancelled) {
          setCampaign(await publicRes.json());
          if (questionsRes.ok) {
            const qData = await questionsRes.json();
            const qs = qData.questions;
            if (qs && qs.length >= 10) {
              setPrefilledQuestions(qs);
            } else {
              // Questions not ready yet (background generation in progress).
              // Start polling until they appear.
              setQuestionsPolling(true);
            }
          } else {
            throw new Error('Unable to load the assigned question set. Please reload.');
          }
        }
      } catch (ex) {
        if (!cancelled) setCampaignError(ex.message);
      } finally {
        if (!cancelled) setCampaignLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [campaignId, currentUser]);

  // 1b. Poll /questions every 10 s while the background generation is still running.
  //     Stops as soon as we get a full question set (or after 20 attempts = ~3 min).
  useEffect(() => {
    if (!questionsPolling) return;
    let attempts = 0;
    const id = setInterval(async () => {
      attempts++;
      if (attempts > 20) { clearInterval(id); return; }
      try {
        const token = await getAuthToken();
        const res = await fetch(`${API_BASE}/api/institutional/campaigns/${campaignId}/questions`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const qData = await res.json();
          const qs = qData.questions;
          if (qs && qs.length >= 10) {
            setPrefilledQuestions(qs);
            setQuestionsPolling(false);
            clearInterval(id);
          }
        }
      } catch (_) {}
    }, 10000);
    return () => clearInterval(id);
  }, [campaignId, questionsPolling]);

  // 2. Firebase auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => setCurrentUser(user));
    return unsub;
  }, []);

  // 3. Redirect to login if not authenticated
  useEffect(() => {
    if (currentUser === null) {
      navigate("/login", { state: { returnTo: `/inst/mcq/${campaignId}` } });
    }
  }, [currentUser, campaignId, navigate]);

  // 4. Once authenticated, check if candidate already completed this campaign
  useEffect(() => {
    if (!currentUser) return;
    async function checkStatus() {
      try {
        const token = await getAuthToken();
        const res = await fetch(
          `${API_BASE}/api/institutional/campaigns/${campaignId}/my-status`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await res.json();
        setAlreadyAttempted(data.attempted === true);
      } catch {
        setAlreadyAttempted(false); // on error, let them proceed
      }
    }
    checkStatus();
  }, [currentUser, campaignId]);

  // 5. Proctoring — enabled once the test has started; gaze tracking when camera granted
  const { warning, integrityScore, integrityLevel, signals, isFullscreenActive, isScreenShareLive } = useCampaignProctoring({
    enabled: testStarted && !assessmentDone,
    campaignId,
    mediaStream: cameraStream,
    screenStream,
  });

  // Live-state-driven proctoring enforcement — ONE combined overlay covering
  // both fullscreen and screen-share, not two independent counter-threshold
  // overlays (which could collide: clicking the browser's native "Stop
  // sharing" control also forces an exit from fullscreen as a browser-level
  // side effect, so a single candidate action could trip both at once, and
  // only the top-stacked overlay was ever visible/clickable). Deliberately
  // not gated on outerPhase === "mcq" — it used to be, which meant a
  // candidate who exited fullscreen or stopped sharing during the coding
  // half of a combined campaign got no warning at all for the rest of the
  // session. Matches the same fix in CompanyAssessmentRoute.jsx.
  //
  // Deliberately no auto-submit-after-N-violations (there used to be one,
  // reusing setDeadlineEpoch to force an early auto-submit). Resolving
  // screen-share opens a native browser picker, which can itself
  // momentarily kick the candidate back out of fullscreen as a side
  // effect — so legitimately fixing ONE violation could coincidentally trip
  // the OTHER a moment later, registering as a second "strike" and silently
  // force-submitting the candidate even though they'd done nothing wrong
  // beyond the original, already-being-corrected violation. The overlay
  // itself is the enforcement (can't interact with the test while
  // non-compliant); every violation still increments fullscreenExitCount /
  // screenShareStoppedCount into the integrity score for the recruiter.
  const fullscreenBroken = fullscreenStatus === "active" && !isFullscreenActive;
  const screenShareBroken = screenShareStatus === "active" && !isScreenShareLive;
  const midTestBlocked = fullscreenBroken || screenShareBroken;

  const handleReturnToFullscreen = async () => {
    try { await document.documentElement.requestFullscreen(); } catch (_) {}
  };

  const statusChecked = alreadyAttempted !== null;
  const ready = !campaignLoading && currentUser && !campaignError && statusChecked;

  // ── Loading / error screens ────────────────────────────────────────────────
  if (!ready || currentUser === undefined || currentUser === null) {
    return (
      <div className="inst-mcq-page">
        <CampaignTopBar />
        <div className="inst-mcq-loading">
          {campaignError ? null : "Loading assessment…"}
        </div>
        {campaignError && (
          <div className="inst-mcq-error-card">
            <div className="inst-mcq-error-icon">⚠️</div>
            <h2>Assessment Unavailable</h2>
            <p>{campaignError}</p>
          </div>
        )}
      </div>
    );
  }

  // ── Questions still generating in the background ─────────────────────────
  if (questionsPolling) {
    return (
      <div className="inst-mcq-page">
        <CampaignTopBar />
        <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
          <div className="inst-mcq-error-icon" style={{ fontSize: "2rem" }}>⏳</div>
          <h2>Preparing Your Assessment</h2>
          <p>Questions are being generated for this test. This usually takes 1–2 minutes.</p>
          <p style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.45)", marginTop: "0.5rem" }}>
            This page will update automatically — please wait.
          </p>
        </div>
      </div>
    );
  }

  // ── Already completed ──────────────────────────────────────────────────────
  if (alreadyAttempted) {
    return (
      <div className="inst-mcq-page">
        <CampaignTopBar />
        <div className="inst-already-done">
          <div className="inst-already-done__icon">✓</div>
          <h2>Assessment Already Submitted</h2>
          <p>You have already completed this assessment. Each link can only be attempted once.</p>
          <p className="inst-already-done__sub">If you believe this is an error, please contact your coordinator.</p>
        </div>
      </div>
    );
  }

  const config = campaign.config || {};
  const prefilledConfig = {
    role: config.role || "General Engineering",
    experience: config.experience || "0",
    difficulty:
      (config.difficulty || "intermediate").charAt(0).toUpperCase() +
      (config.difficulty || "intermediate").slice(1),
    category: config.category || "generic",
    company: campaign.college_name || "",
    jdText: "",
  };

  // has_coding comes from the lightweight metadata — coding problems are fetched lazily.
  const hasCoding = campaign.has_coding || false;

  // ── Coding submit handler ─────────────────────────────────────────────────
  const handleCodingSubmit = async (submissions) => {
    const token = await getAuthToken();
    if (!token) throw new Error('Please sign in before submitting.');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 60000);
    const payload = JSON.stringify({ submissions });
    try {
      const res = await fetch(`${API_BASE}/api/institutional/campaigns/${campaignId}/coding-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: payload, signal: controller.signal, keepalive: new Blob([payload]).size < 60000,
      });
      const receipt = await res.json();
      if (!res.ok) throw new Error(receipt.detail || 'Unable to save submission');
      setAssessmentDone(true);
      return receipt;
    } finally {
      clearTimeout(timer);
    }
  };

  // ── Transition to coding phase: fetch problems first, then switch view ────
  const handleMcqComplete = async (score) => {
    if (!hasCoding) { setAssessmentDone(true); return; }
    setMcqScore(score || null);
    // Reset camera stream and status so the old MCQ stream (whose tracks are
    // stopped on SelfViewCamera unmount) is not passed to useCampaignProctoring.
    // A new SelfViewCamera in the coding phase will set a fresh stream + status.
    setCameraStream(null);
    setCameraStatus(null);
    try {
      const res = await fetch(`${API_BASE}/api/institutional/campaigns/${campaignId}/coding`);
      if (res.ok) {
        const data = await res.json();
        setCodingProblems(data.problems || []);
      }
    } catch (_) { /* silently fall through — CodingTest handles empty problems */ }
    setOuterPhase("coding");
  };

  // ── Camera denied gate ────────────────────────────────────────────────────
  if (cameraStatus === "denied") {
    return (
      <div className="inst-mcq-page">
        <CampaignTopBar />
        <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
          <div className="inst-mcq-error-icon">📷</div>
          <h2>Camera Access Required</h2>
          <p>
            This assessment requires camera access for proctoring. Please allow
            camera access in your browser settings, then reload the page.
          </p>
          <button
            className="inst-btn-primary"
            style={{ marginTop: "1.25rem" }}
            onClick={() => window.location.reload()}
          >
            Reload &amp; Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Screen-share + fullscreen gate (MCQ phase only) ───────────────────────
  const proctoringGateComplete =
    screenShareStatus === "active" && (fullscreenStatus === "active" || fullscreenStatus === "skipped");
  if (outerPhase === "mcq" && !proctoringGateComplete) {
    return (
      <div className="inst-mcq-page">
        <CampaignTopBar />
        <ProctoringGateCard
          screenShareStatus={screenShareStatus}
          onShareScreen={handleShareScreen}
          fullscreenStatus={fullscreenStatus}
          fullscreenAttempts={fullscreenAttempts}
          onEnterFullscreen={handleEnterFullscreen}
          onSkipFullscreen={() => setFullscreenStatus("skipped")}
        />
      </div>
    );
  }

  // ── Coding phase ─────────────────────────────────────────────────────────
  if (outerPhase === "coding") {
    return (
      <div className="inst-topbar-offset" style={{ position: "relative" }}>
        <CampaignTopBar integrityScore={integrityScore} integrityLevel={integrityLevel} deadlineEpoch={deadlineEpoch} />
        {warning && (
          <div className="inst-proctor-banner">
            <span className="inst-proctor-icon">⚠</span>
            {warning}
          </div>
        )}
        {!assessmentDone && <SelfViewCamera onStream={setCameraStream} onStatus={setCameraStatus} />}
        <CodingTest
          campaign={{ title: campaign.title, college_name: campaign.college_name }}
          problems={codingProblems}
          onSubmitAll={handleCodingSubmit}
          mcqScore={mcqScore}
          campaignId={campaignId}
          deadlineEpoch={deadlineEpoch}
          hideInternalTimer
        />
        {midTestBlocked && (
          <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "grid", placeItems: "center", background: "rgba(5,3,15,0.85)", backdropFilter: "blur(6px)" }}>
            <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
              <div className="inst-mcq-error-icon">⚠️</div>
              <h2>Proctoring Requirement Not Met</h2>
              {fullscreenBroken && <p>You exited fullscreen mode.</p>}
              {screenShareBroken && <p>You stopped sharing your screen.</p>}
              <p style={{ color: "#f87171", fontSize: "0.85rem" }}>
                This has been recorded as a proctoring violation. If this happens again, your test will be submitted automatically.
              </p>
              <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "1.25rem", flexWrap: "wrap" }}>
                {fullscreenBroken && (
                  <button className="inst-btn-primary" onClick={handleReturnToFullscreen}>Return to Fullscreen</button>
                )}
                {screenShareBroken && (
                  <button className="inst-btn-primary" onClick={handleShareScreen}>Resume Screen Sharing</button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── MCQ phase ─────────────────────────────────────────────────────────────
  return (
    <div className="inst-mcq-assessment-wrap inst-topbar-offset" style={{ position: "relative" }}>
      <CampaignTopBar integrityScore={testStarted ? integrityScore : undefined} integrityLevel={integrityLevel} deadlineEpoch={deadlineEpoch} />

      {warning && (
        <div className="inst-proctor-banner">
          <span className="inst-proctor-icon">⚠</span>
          {warning}
        </div>
      )}

      {testStarted && <SelfViewCamera onStream={setCameraStream} onStatus={setCameraStatus} />}

      <AssessmentScreen
        prefilledConfig={prefilledConfig}
        prefilledQuestions={prefilledQuestions}
        campaignId={campaignId}
        onTestStart={() => {
          setTestStarted(true);
          setDeadlineEpoch((prev) => {
            if (prev) return prev; // already set (page refresh case)
            const epoch = Date.now() + 65 * 60 * 1000;
            sessionStorage.setItem(`vagmee_deadline_${campaignId}`, String(epoch));
            return epoch;
          });
        }}
        skipScorecard={hasCoding}
        onComplete={handleMcqComplete}
        deadlineEpoch={deadlineEpoch}
      />

      {midTestBlocked && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "grid", placeItems: "center", background: "rgba(5,3,15,0.85)", backdropFilter: "blur(6px)" }}>
          <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
            <div className="inst-mcq-error-icon">⚠️</div>
            <h2>Proctoring Requirement Not Met</h2>
            {fullscreenBroken && <p>You exited fullscreen mode.</p>}
            {screenShareBroken && <p>You stopped sharing your screen.</p>}
            <p style={{ color: "#f87171", fontSize: "0.85rem" }}>
              This has been recorded as a proctoring violation. If this happens again, your test will be submitted automatically.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "1.25rem", flexWrap: "wrap" }}>
              {fullscreenBroken && (
                <button className="inst-btn-primary" onClick={handleReturnToFullscreen}>Return to Fullscreen</button>
              )}
              {screenShareBroken && (
                <button className="inst-btn-primary" onClick={handleShareScreen}>Resume Screen Sharing</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
